import type { TranslateFn } from '@newo/localization';
import { phoneFieldSchema } from '@newo/ui';
import { z } from 'zod';
import { StepperIndustryKey, type StepperOnboardingFormValues } from './types';

type StepperDefaultFormValueOverrides = Partial<
	Pick<
		StepperOnboardingFormValues,
		'industry' | 'managerFullName' | 'websiteUrl'
	>
>;

interface StepperValidationConfig {
	translate: TranslateFn;
}

export const createStepperDefaultFormValues = (
	translate: TranslateFn,
	overrides: StepperDefaultFormValueOverrides = {}
): StepperOnboardingFormValues => ({
	agentName: translate('creator', 'stepperOnboarding.profile.agentName'),
	agentPhoneNumber: '',
	businessName: '',
	businessPhoneNumber: '',
	createdCustomerIdn: '',
	industry: overrides.industry ?? StepperIndustryKey.AutoDetect,
	websiteUrl: overrides.websiteUrl ?? '',
	greetingPhrase: translate(
		'creator',
		'stepperOnboarding.steps.customizeGreeting.greetingValue'
	),
	managerEmail: '',
	managerFullName: overrides.managerFullName ?? '',
	managerPhone: ''
});

export const createStepperOnboardingSchema = ({
	translate
}: StepperValidationConfig) =>
	z.object({
		agentName: z.string(),
		agentPhoneNumber: z.string(),
		businessName: z.string(),
		businessPhoneNumber: z.string(),
		createdCustomerIdn: z.string(),
		industry: z.nativeEnum(StepperIndustryKey),
		websiteUrl: z
			.string()
			.trim()
			.min(
				1,
				translate(
					'creator',
					'stepperOnboarding.steps.extractWebsiteContent.websiteRequired'
				)
			),
		greetingPhrase: z.string(),
		managerEmail: z
			.string()
			.trim()
			.email(
				translate(
					'creator',
					'stepperOnboarding.steps.callSummariesTransfers.managerEmailError'
				)
			),
		managerFullName: z
			.string()
			.trim()
			.min(
				1,
				translate(
					'creator',
					'stepperOnboarding.steps.callSummariesTransfers.managerFullNameError'
				)
			),
		managerPhone: phoneFieldSchema({
			required: translate('common', 'phone_required'),
			invalidMessage: translate(
				'creator',
				'stepperOnboarding.steps.callSummariesTransfers.managerPhoneError'
			)
		})
	});
